import React, { useEffect, useState } from "react";
import skuAttributeService from "../../masters/sku-attributes/skuAttributeService";
import axiosClient from "../../../../../api/axiosClient";

const AttributeSelector = ({ entityType, entityId, value = [], onChange, refreshKey }) => {

  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [engSkus, setEngSkus] = useState([]);

  // ============================================================
  // FETCH ATTRIBUTES
  // ============================================================
  useEffect(() => {

    const fetchAttributes = async () => {

      console.log("🔵 STEP A - FETCH ATTRIBUTES START", { entityType, entityId });

      // ========================================================
      // 🟢 CREATE MODE → LOAD REQUIRED ATTRIBUTES ONLY
      // ========================================================
      if (!entityId) {
        console.log("🟡 CREATE MODE → Load REQUIRED attributes");

        try {
          setLoading(true);

          const res = await axiosClient.get(
            `/api/ppe/sku-attributes/entity-required/${entityType}`
          );

          if (!res.data?.success) {
            setAttributes([]);
            return;
          }

          const normalized = (res.data.data || []).map(a => ({
            attribute_id: a.id,
            attribute_code: a.attribute_code,
            attribute_name: a.attribute_name,
            data_type: a.data_type,
            is_required: a.is_required,
            values: a.values || []
          }));

          setAttributes(normalized);

        } catch (err) {
          console.error("❌ ERROR loading required attributes", err);
          setAttributes([]);
        } finally {
          setLoading(false);
        }

        return;
      }

      // ========================================================
      // 🟢 EDIT MODE → MERGE MASTER + VALUES
      // ========================================================
      try {
        setLoading(true);

        // MASTER
        const masterRes = await axiosClient.get(
          `/api/ppe/sku-attributes/entity/${entityType}`
        );

        // VALUES
        const valueRes = await skuAttributeService.getMappedAttributes(
          entityType,
          entityId
        );

        const masterData = masterRes.data?.data || [];

        let valueData = [];
        if (Array.isArray(valueRes)) {
          valueData = valueRes;
        } else if (Array.isArray(valueRes?.data)) {
          valueData = valueRes.data;
        } else if (Array.isArray(valueRes?.data?.data)) {
          valueData = valueRes.data.data;
        }

        console.log("FINAL VALUE DATA:", valueData);
        console.log("MASTER:", masterData);

        const merged = masterData.map(attr => {
          const existing = valueData.find(
            v => v.attribute_id === attr.id
          );

          return {
            attribute_id: attr.id,
            attribute_code: attr.attribute_code,
            attribute_name: attr.attribute_name,
            data_type: attr.data_type,
            is_required: attr.is_required,
            values: attr.values || [],
            value: existing?.selected_value || existing?.value || ""
          };
        });

        setAttributes(merged);

        // Sync parent state
        if ((value || []).length === 0) {
          const initialValues = merged
            .filter(a => a.value)
            .map(a => ({
              attribute_id: a.attribute_id,
              value: a.value
            }));

          if (initialValues.length > 0) {
            onChange(initialValues);
          }
        }

      } catch (err) {
        console.error("❌ ERROR loading edit attributes", err);
        setAttributes([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAttributes();

  }, [entityType, entityId, refreshKey]);

  // ============================================================
  // LOAD ENG SKUs
  // ============================================================
  useEffect(() => {
    const loadEngSkus = async () => {
      try {
        const res = await axiosClient.get(
          "/api/ppe/masters/raw-materials/eng-skus"
        );
        setEngSkus(res.data?.data || []);
      } catch (err) {
        console.error("❌ Failed to load ENG SKUs", err);
      }
    };

    loadEngSkus();
  }, []);

  // ============================================================
  // HANDLE CHANGE
  // ============================================================
  const handleChange = (attributeId, val) => {

    const updated = [...(value || [])];

    const index = updated.findIndex(a => a.attribute_id === attributeId);

    if (index >= 0) {
      updated[index].value = val;
    } else {
      updated.push({
        attribute_id: attributeId,
        value: val
      });
    }

    onChange(updated);
  };

  // ============================================================
  // UI
  // ============================================================
  return (
    <div className="attr-container">

      <h4 className="attr-title">Attributes</h4>

      {loading ? (
        <div className="empty">Loading attributes...</div>
      ) : attributes.length === 0 ? (
        <div className="empty">No attributes mapped</div>
      ) : (

        <div className="attr-grid">
          {attributes.map(attr => {

            // ✅ FINAL FIXED VALUE BINDING
            const existing = {
              value:
                attributes.find(v => v.attribute_id === attr.attribute_id)?.value ||
                value.find(v => v.attribute_id === attr.attribute_id)?.value ||
                attr.value ||
                ""
            };

            return (
              <div key={attr.attribute_id} className="attr-field">

                <label>
                  {attr.attribute_name}
                  {attr.is_required && <span className="required">*</span>}
                </label>

                {attr.attribute_code === "WITHOUT_MANUFACTURER_SKU_CODE" ? (

                  <select
                    className="input"
                    value={existing.value || ""}
                    onChange={(e) =>
                      handleChange(attr.attribute_id, e.target.value)
                    }
                  >
                    <option value="">Select Internal SKU</option>
                    {engSkus.map(sku => (
                      <option key={sku.id} value={sku.code}>
                          {sku.code} - {sku.name}
                      </option>
                    ))}
                  </select>

                ) : (

                  <>
                    {attr.data_type === "TEXT" && (
                      <input
                        className="input"
                        value={existing.value || ""}
                        onChange={(e) =>
                          handleChange(attr.attribute_id, e.target.value)
                        }
                      />
                    )}

                    {attr.data_type === "NUMBER" && (
                      <input
                        type="number"
                        className="input"
                        value={existing.value || ""}
                        onChange={(e) =>
                          handleChange(attr.attribute_id, e.target.value)
                        }
                      />
                    )}

                    {attr.data_type === "LIST" && (
                      <select
                        className="input"
                        value={existing.value || ""}
                        onChange={(e) =>
                          handleChange(attr.attribute_id, e.target.value)
                        }
                      >
                        <option value="">Select</option>
                        {attr.values?.map(v => (
                          <option key={v.id} value={v.value}>
                            {v.value}
                          </option>
                        ))}
                      </select>
                    )}

                    {attr.data_type === "BOOLEAN" && (
                      <input
                        type="checkbox"
                        checked={existing.value || false}
                        onChange={(e) =>
                          handleChange(attr.attribute_id, e.target.checked)
                        }
                      />
                    )}
                  </>
                )}

              </div>
            );
          })}
        </div>

      )}

      <style>{`
        .attr-container {
          margin-top: 20px;
          padding: 16px;
          background: #fff;
          border-radius: 8px;
          box-shadow: 0 1px 4px rgba(0,0,0,0.08);
        }

        .attr-title {
          margin-bottom: 12px;
        }

        .attr-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        .attr-field {
          display: flex;
          flex-direction: column;
        }

        .input {
          padding: 8px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          margin-top: 4px;
        }

        .required {
          color: red;
          margin-left: 4px;
        }

        .empty {
          color: #6b7280;
        }

        @media (max-width: 768px) {
          .attr-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

    </div>
  );
};

export default AttributeSelector;